import React, { useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toBlob, toPng } from 'html-to-image';
import triviaData from '../assets/bar_mitzva_trivia.json';
import parashaList from '../../parasha_map.json';
import bgTexture from '../assets/webp/trivia-result-bg.webp';
import cheeringKid from '../assets/webp/cheering-kid.webp';
import qrCode from '../assets/webp/trivia-result-qr.webp';
import iconPlay from '../assets/icon-play.svg';
import iconRepeat from '../assets/webp/icon-repeat.webp';
import logo from '../assets/webp/logo.webp';
import BottomNav from '../components/BottomNav';
import { getParasha, StoredParasha } from '../utils/userSelections';

const CLOUDFRONT_BASE = 'https://d1q1chclchjxl4.cloudfront.net';

interface ResultState {
  score: number;
  total: number;
  parasha: string;
  parashaEnglish?: string;
}

// Spelling variants between the trivia file and parasha_map.json (extra vav/yod)
const HEBREW_SPELLING_ALIASES: Record<string, string> = {
  תצווה: 'תצוה',
  קורח: 'קרח',
  פינחס: 'פנחס',
  ניצבים: 'נצבים',
};

const sameParasha = (a: string, b: string): boolean =>
  a === b || HEBREW_SPELLING_ALIASES[a] === b || HEBREW_SPELLING_ALIASES[b] === a;

const findParashaByHebrew = (hebrew: string): StoredParasha | null =>
  (parashaList as StoredParasha[]).find((p) => sameParasha(p.hebrew, hebrew)) ?? null;

/**
 * Next parasha in the Torah-ordered trivia list. Wraps around to the first
 * parasha after the last one, or when the current parasha is unknown.
 */
const getNextChallenge = (currentHebrew: string): StoredParasha => {
  const order = triviaData.parshiyot.map((p) => p.parsha);
  const idx = order.findIndex((h) => sameParasha(h, currentHebrew));
  // Keep the trivia file's spelling so the quiz page finds the questions
  const nextHebrew = order[(idx + 1) % order.length];
  return { hebrew: nextHebrew, english: findParashaByHebrew(nextHebrew)?.english ?? '' };
};

const TriviaResultPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const stored = getParasha();
  // Fall back to the user's stored parasha when the page is opened without quiz state
  const state = (location.state as ResultState | null) ?? {
    score: 0,
    total: 10,
    parasha: stored?.hebrew ?? '',
    parashaEnglish: stored?.english,
  };
  const { score, total, parasha, parashaEnglish } = state;

  const [heroError, setHeroError] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const shareAreaRef = useRef<HTMLDivElement>(null);

  const english =
    parashaEnglish ??
    findParashaByHebrew(parasha)?.english ??
    (stored && sameParasha(stored.hebrew, parasha) ? stored.english : undefined);

  const heroImageUrl = english ? `${CLOUDFRONT_BASE}/images/${english.toLowerCase()}.webp` : null;

  const pct = total > 0 ? Math.round((score / total) * 100) : 0;
  const nextChallenge = getNextChallenge(parasha);

  const shareText = t('trivia_result_share_text', {
    score,
    total,
    parasha,
    defaultValue: `קיבלת ${score}/${total} בפרשת ${parasha} בקול תורה. כמה אתם תצליחו?! 🚀`,
  });

  type FlutterBridge = { callHandler: (name: string, ...args: unknown[]) => Promise<unknown> };
  const flutterBridge = (window as Window & { flutter_inappwebview?: FlutterBridge })
    .flutter_inappwebview;

  const shareTitle =
    t('trivia_result_share_title', { parasha, defaultValue: `חידון פרשת ${parasha}` }) ?? undefined;

  const captureOptions = { cacheBust: true, pixelRatio: 2, backgroundColor: '#02234c' };

  /** Fall back to sharing plain text when the page can't be captured as an image. */
  const shareAsText = async (): Promise<void> => {
    const shareData: ShareData = { title: shareTitle, text: shareText, url: CLOUDFRONT_BASE };
    if (navigator.share && navigator.canShare?.(shareData)) {
      await navigator.share(shareData);
    } else if (navigator.share) {
      await navigator.share({ text: shareText });
    } else {
      await navigator.clipboard.writeText(shareText);
    }
  };

  const handleShare = async (): Promise<void> => {
    if (isSharing) return;
    setIsSharing(true);
    try {
      const node = shareAreaRef.current;

      if (flutterBridge) {
        const dataUrl = node
          ? await toPng(node, captureOptions).catch((err: unknown) => {
              console.error('Page capture failed:', err);
              return null;
            })
          : null;
        await flutterBridge.callHandler(
          'nativeShare',
          dataUrl ? { image: dataUrl, text: shareText } : { text: shareText },
        );
        return;
      }

      const blob = node
        ? await toBlob(node, captureOptions).catch((err: unknown) => {
            console.error('Page capture failed:', err);
            return null;
          })
        : null;

      if (blob) {
        const file = new File([blob], 'trivia-result.png', { type: 'image/png' });
        const shareData: ShareData = { files: [file], title: shareTitle, text: shareText };
        if (navigator.share && navigator.canShare?.(shareData)) {
          await navigator.share(shareData);
          return;
        }
      }

      await shareAsText();
    } catch (err) {
      if ((err as Error).name !== 'AbortError') {
        console.error('Share failed:', err);
      }
    } finally {
      setIsSharing(false);
    }
  };

  const handleAnswerAgain = (): void => {
    navigate('/trivia-quiz', {
      state: english ? { parasha: { hebrew: parasha, english } } : undefined,
    });
  };

  const handleStartNext = (): void => {
    navigate('/trivia-quiz', { state: { parasha: nextChallenge } });
  };

  return (
    <div className="flex flex-1 flex-col overflow-hidden" dir="rtl">
      <div
        ref={shareAreaRef}
        className="flex flex-1 flex-col overflow-y-auto bg-[#02234c] bg-cover bg-center"
        style={{ backgroundImage: `url(${bgTexture})` }}
      >
        {/* Parasha hero image */}
        {heroImageUrl && !heroError && (
          <img
            src={heroImageUrl}
            alt={parasha}
            className="h-44 w-full shrink-0 object-cover"
            onError={() => setHeroError(true)}
          />
        )}

        {/* Kol Torah logo in a white circle, overlapping the hero */}
        <div
          className={`z-10 mx-auto flex h-28 w-28 shrink-0 items-center justify-center rounded-full bg-white shadow-lg ${
            heroImageUrl && !heroError ? '-mt-14' : 'mt-6'
          }`}
        >
          <img src={logo} alt="" className="h-24 w-24 object-contain" />
        </div>

        {/* Title */}
        <p className="mt-8 px-4 text-center font-bubbles text-4xl text-white [text-shadow:0_8px_4px_rgba(0,0,0,0.62)]">
          {t('trivia_result_finished_quiz', 'סיימת את החידון!')}
        </p>

        {/* Score */}
        <p className="mt-6 text-center font-bubbles text-2xl text-white [text-shadow:0_8px_4px_rgba(0,0,0,0.62)]">
          {t('trivia_result_your_score', 'הציון שלך:')}
        </p>
        <p className="text-center font-bubbles text-[6.5rem] leading-none text-[#f2cc0d]">{pct}%</p>
        <p className="mt-2 text-center font-bubbles text-xl text-white">
          {t('trivia_result_correct_answers', {
            score,
            total,
            defaultValue: `${score}/${total} תשובות נכונות`,
          })}
        </p>

        {/* Next challenge */}
        <p className="mt-8 px-4 text-center font-bubbles text-3xl">
          <span className="text-white">{t('trivia_result_next_challenge', 'אתגר הבא:')}</span>{' '}
          <span className="text-[#f2cc0d]">
            {t('trivia_result_next_challenge_parasha', {
              parasha: nextChallenge.hebrew,
              defaultValue: `פרשת ${nextChallenge.hebrew}`,
            })}
          </span>
        </p>

        {/* Action buttons */}
        <div className="mt-4 flex items-center justify-center gap-1.5 px-2">
          <button
            onClick={() => navigate('/user-portal')}
            className="whitespace-nowrap rounded-[13px] bg-white px-3 py-2.5 font-bubbles text-sm text-[#001c3c] shadow-md transition-transform active:scale-95"
          >
            {t('trivia_result_all_challenges', 'לכל האתגרים')}
          </button>
          <button
            onClick={handleStartNext}
            className="flex items-center gap-1.5 whitespace-nowrap rounded-[13px] bg-[#f2cc0d] px-4 py-2.5 font-bubbles text-lg text-[#001c3c] shadow-md transition-transform active:scale-95"
          >
            <span>{t('trivia_result_start', 'התחל')}</span>
            <img src={iconPlay} alt="" className="h-5 w-5" />
          </button>
          <button
            onClick={handleAnswerAgain}
            className="flex items-center gap-1 whitespace-nowrap rounded-[10px] border border-black bg-white px-2.5 py-1.5 font-bubbles text-sm text-[#001c3c] transition-transform active:scale-95"
          >
            <span>{t('trivia_result_answer_again', 'ענה מחדש')}</span>
            <img src={iconRepeat} alt="" className="h-4 w-4" />
          </button>
        </div>

        {/* Cheering boy + QR / share */}
        <div className="mt-auto flex items-end justify-between gap-2 px-4 pt-8">
          <div className="flex shrink-0 flex-col items-center gap-2 pb-3">
            <p className="font-bubbles text-xl text-white">
              {t('trivia_result_scan_to_download', 'סרוק להורדה')}
            </p>
            <img src={qrCode} alt="" className="h-28 w-28 rounded-xl bg-white p-1.5" />
            <button
              onClick={handleShare}
              disabled={isSharing}
              className="mt-2 rounded-[14px] bg-white px-7 py-1.5 font-bubbles text-base text-[#001c3c] shadow-md transition-transform active:scale-95 disabled:opacity-60"
            >
              {t('trivia_result_share_button', 'שתף')}
            </button>
          </div>
          <img src={cheeringKid} alt="" className="w-56 max-w-[55%] object-contain" />
        </div>
      </div>

      <BottomNav />
    </div>
  );
};

export default TriviaResultPage;
